import type { ReactNode } from 'react';
import { cn } from './lib/cn';

// Visual treatment mirrors @immo/ui-kit's <Input> exactly (h-control-md,
// bg-surface, border-border, text-base, focus-visible:border-interactive
// + shadow-focus) so a Select sitting next to an Input has the same
// height and chrome.

type NativeSelectOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
};

export type NativeSelectOptionGroup = {
  readonly label: string;
  readonly options: ReadonlyArray<NativeSelectOption>;
};

type NativeSelectProps = {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly placeholder?: string;
  // 'md' (default) matches an <Input>; 'sm' is a compact variant for dense
  // surfaces like toolbars or an inline model/conversation picker.
  readonly size?: 'sm' | 'md';
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
  // An element painted at the leading edge of the control — a status dot, an
  // icon. Opt-in: without it the select renders exactly as it always has, so no
  // existing caller inherits the extra padding it needs (BL-062). The caller
  // positions it and marks it `aria-hidden` / `pointer-events-none`.
  readonly leadingAdornment?: ReactNode;
  // Flat options render first, then groups, then trailing options.
  readonly options?: ReadonlyArray<NativeSelectOption>;
  readonly groups?: ReadonlyArray<NativeSelectOptionGroup>;
  readonly trailingOptions?: ReadonlyArray<NativeSelectOption>;
};

const SIZE_CLASSES = {
  md: 'h-control-md pl-3 pr-9 py-2 text-base',
  sm: 'h-control-sm pl-2.5 pr-8 py-1 text-sm',
} as const;

// Leading padding widened to clear an adornment sitting at the same inset the
// chevron uses on the trailing side.
const ADORNED_PADDING = { md: 'pl-8', sm: 'pl-7' } as const;

export function NativeSelect(props: NativeSelectProps) {
  const {
    id,
    value,
    onChange,
    disabled = false,
    invalid = false,
    options,
    groups,
    trailingOptions,
    placeholder,
    size = 'md',
    leadingAdornment,
  } = props;
  const ariaDescribedBy = props['aria-describedby'];
  const ariaLabel = props['aria-label'];

  return (
    <div className="relative">
      {leadingAdornment}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className={cn(
          'flex w-full appearance-none rounded-md border bg-surface font-sans text-text-primary',
          SIZE_CLASSES[size],
          leadingAdornment !== undefined && ADORNED_PADDING[size],
          'transition-colors duration-micro ease-out',
          'focus-visible:outline-none focus-visible:border-interactive focus-visible:shadow-focus',
          'disabled:cursor-not-allowed disabled:opacity-60',
          invalid
            ? 'border-danger-500 focus-visible:border-danger-500 focus-visible:shadow-focus-danger'
            : 'border-border',
        )}
      >
        {placeholder !== undefined && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options?.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled === true}>
            {opt.label}
          </option>
        ))}
        {groups?.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled === true}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}
        {trailingOptions?.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled === true}>
            {opt.label}
          </option>
        ))}
      </select>
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          'pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary',
          size === 'sm' ? 'right-2.5' : 'right-3',
        )}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}
